import Image from 'next/image'
import { FunctionComponent } from 'react'
import { useMount } from 'react-use'
import styled from 'styled-components'
import routes from '@/hooks/use-authorization/routes'
import { OurIntlShape } from '@/i18n/typing'
import PlatformImage from '@/utils/export-pptx/components/platform-image'
import { designLayoutSize, PptxObjectType } from '@/utils/export-pptx/constants'
import { CooperationContentSlide } from '@/utils/export-pptx/types/slide'
import { getThumbnail } from '@/utils/get-thumbnails'

interface SlideProps {
  slide: CooperationContentSlide
  onSlideReady?: (slide: CooperationContentSlide) => void
  formatMessage: OurIntlShape['formatMessage']
}

const SlideComponent: FunctionComponent<SlideProps> = ({
  slide,
  onSlideReady,
  formatMessage,
}) => {
  useMount(() => {
    onSlideReady?.(slide)
  })

  return (
    <Wrapper data-pptx-object={PptxObjectType.Shape}>
      <TitleBlock>
        <Title data-pptx-object={PptxObjectType.Text}>
          {formatMessage({ id: 'general:pptx_title_cooperation_content' })}
        </Title>
        <TopBrandLine data-pptx-object={PptxObjectType.Shape} />
      </TitleBlock>
      <BottomBrandLine data-pptx-object={PptxObjectType.Shape} />
      <Costs>
        <CostsTitle data-pptx-object={PptxObjectType.Text}>
          {formatMessage({ id: 'general:field_total_budget' })}
        </CostsTitle>
        <CostsValue data-pptx-object={PptxObjectType.Text}>
          {slide.payload.budget}
        </CostsValue>
      </Costs>
      <Portfolios>
        <PortfoliosTitle data-pptx-object={PptxObjectType.Text}>
          {formatMessage({ id: 'general:field_kol_portfolios' })}
        </PortfoliosTitle>
        <PortfoliosValue data-pptx-object={PptxObjectType.Text}>
          {slide.payload.kolPortfolio}
        </PortfoliosValue>
      </Portfolios>
      <KolCardWrapper>
        {slide.payload.kolList.map((kol, index) => (
          <KolCard data-pptx-object={PptxObjectType.Shape} key={index}>
            <KolCardPerson>
              <Image
                priority
                unoptimized
                alt='kol thumbnail'
                data-link={routes.kolId.stringifyUrl({ kolId: kol.uuid })}
                data-pptx-object={PptxObjectType.Image}
                height={40}
                src={getThumbnail(kol.uuid)}
                style={{ borderRadius: '50%' }}
                width={40}
              />
              <KolCardPersonName data-pptx-object={PptxObjectType.Text}>
                {kol.name}
              </KolCardPersonName>
            </KolCardPerson>
            <KolCardPlatformList>
              {kol.items.map((item) => (
                <KolCardPlatform key={item.platform}>
                  <PlatformImage
                    height={14}
                    platform={item.platform}
                    width={14}
                  />
                  <span data-pptx-object={PptxObjectType.Text}>
                    {item.content}
                  </span>
                </KolCardPlatform>
              ))}
            </KolCardPlatformList>
          </KolCard>
        ))}
      </KolCardWrapper>
    </Wrapper>
  )
}

const BrandLine = styled.div`
  position: absolute;
  background: ${({ theme }): string => theme.colors.brand.primary};
  height: 1px;
`

const TopBrandLine = styled(BrandLine)`
  position: static;
  top: 72px;
  left: 358px;
  right: 64px;
`

const BottomBrandLine = styled(BrandLine)`
  bottom: 80px;
  left: 64px;
  right: 64px;
`

const Title = styled.div`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 42px;
  font-weight: 700;
  line-height: 70px;
`

const TitleBlock = styled.div`
  position: absolute;
  top: 37px;
  left: 64px;
  right: 64px;
  display: flex;
  gap: 24px;

  ${Title} {
    flex: 0 0 auto;
  }

  ${TopBrandLine} {
    margin-top: 34px;
    flex: 1 1 auto;
  }
`

const Wrapper = styled.div`
  font-family: Arial;
  width: ${designLayoutSize.w}px;
  height: ${designLayoutSize.h}px;
  position: relative;
  color: ${({ theme }): string => theme.colors.text.primary};
  background-color: ${({ theme }): string => theme.colors.border.divider};

  &,
  * {
    box-sizing: border-box;
  }
`

const KolCardWrapper = styled.div`
  position: absolute;
  left: 474px;
  top: 227px;
  display: flex;
  flex-wrap: wrap;
  gap: 36px;
`

const KolCardPerson = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const KolCardPersonName = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 19px;
`

const KolCardPlatformList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const KolCardPlatform = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  line-height: 18px;
`

const KolCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: ${({ theme }): string => theme.colors.background.white};
  border-radius: 12px;
  padding: 16px 24px;
  width: 261px;
  border: solid 1px ${({ theme }): string => theme.colors.border.border};
`

const CostsTitle = styled.div``

const CostsValue = styled.div``

const Costs = styled.div`
  position: absolute;
  top: 278px;
  left: 148px;
  font-size: 45px;
  font-weight: 700;
  line-height: 72px;
`

const PortfoliosTitle = styled.div`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 18px;
  font-weight: 700;
  line-height: 25px;
`

const PortfoliosValue = styled.div`
  color: ${({ theme }): string => theme.colors.text.secondary};
  font-size: 20px;
  font-weight: 400;
  line-height: 28px;
  width: 306px;
`

const Portfolios = styled.div`
  position: absolute;
  top: 454px;
  left: 148px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export default SlideComponent
